/* global axios */
import ApiClient from './ApiClient';

export const buildContactParams = (page, sortAttr, label, search) => {
  let params = `include_contact_inboxes=false&page=${page}&sort=${sortAttr}`;
  if (search) {
    params = `${params}&q=${search}`;
  }
  if (label) {
    params = `${params}&labels[]=${label}`;
  }
  return params;
};

class ContactAPI extends ApiClient {
  constructor() {
    super('contacts', { accountScoped: true });
  }

  get(page, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}?${buildContactParams(
      page,
      sortAttr,
      label,
      ''
    )}`;
    return axios.get(requestURL);
  }

  show(id) {
    return axios.get(`${this.url}/${id}?include_contact_inboxes=false`);
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}?include_contact_inboxes=false`, data);
  }

  getConversations(contactId, page = 1, status = '') {
    let url = `${this.url}/${contactId}/conversations?page=${page}`;
    if (status) {
      url += `&status=${status}`;
    }
    return axios.get(url);
  }

  getContactableInboxes(contactId) {
    return axios.get(`${this.url}/${contactId}/contactable_inboxes`);
  }

  getContactLabels(contactId) {
    return axios.get(`${this.url}/${contactId}/labels`);
  }

  initiateCall(contactId, inboxId, options = {}) {
    if (!contactId) throw new Error('Contact ID is required');
    const payload = {};
    if (inboxId) payload.inbox_id = inboxId;
    if (options.phoneNumber) payload.phone_number = options.phoneNumber;
    if (options.anonymous) payload.anonymous = options.anonymous;
    if (options.newCall) payload.new_call = options.newCall;
    if (options.conversationId)
      payload.conversation_id = options.conversationId;
    return axios.post(`${this.url}/${contactId}/call`, payload);
  }

  updateContactLabels(contactId, labels) {
    return axios.post(`${this.url}/${contactId}/labels`, { labels });
  }

  search(search = '', page = 1, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}/search?${buildContactParams(
      page,
      sortAttr,
      label,
      search
    )}`;
    return axios.get(requestURL);
  }

  active(page = 1, sortAttr = 'name') {
    let requestURL = `${this.url}/active?${buildContactParams(page, sortAttr)}`;
    return axios.get(requestURL);
  }

  filter(
    page = 1,
    sortAttr = 'name',
    queryPayload = {},
    { skipCount = false } = {}
  ) {
    let requestURL = `${this.url}/filter?${buildContactParams(page, sortAttr)}`;
    // skip_count avoids an unbounded COUNT(*) for typeahead callers that only
    // render the first page of results.
    if (skipCount) {
      requestURL = `${requestURL}&skip_count=true`;
    }
    return axios.post(requestURL, queryPayload);
  }

  importContacts(file) {
    const formData = new FormData();
    formData.append('import_file', file);
    return axios.post(`${this.url}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  destroyCustomAttributes(contactId, customAttributes) {
    return axios.post(`${this.url}/${contactId}/destroy_custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  destroyAvatar(contactId) {
    return axios.delete(`${this.url}/${contactId}/avatar`);
  }

  exportContacts(queryPayload) {
    return axios.post(`${this.url}/export`, queryPayload);
  }

  syncZendesk(contactId) {
    return axios.post(`${this.url}/${contactId}/sync_zendesk`);
  }
}

export default new ContactAPI();
